import { Button } from '@heidi/design-system/button';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Minimize2, X } from 'lucide-react';
import { useIntl } from 'react-intl';

import type { VoiceSessionState } from '../../../contracts/voice-protocol';
import { voiceAssistantMessages } from '../components/voice-assistant.messages';
import { statusDotClass, statusLabel } from '../components/voiceAssistantPanelStatus';
import { VoiceFeedbackThumbs } from '../feedback/VoiceFeedbackThumbs';
import type { VoiceConnectionStatus } from '../store/useVoiceAssistantStore';
import { voiceWorkspaceMessages as messages } from './workspace.messages';
import type { WorkspaceQuickStartKind } from './WorkspaceEmptyState';
import { WorkspaceEmptyState } from './WorkspaceEmptyState';

export function VoiceWindowHeader({
  connection,
  sessionState,
  greetingComplete,
  onMinimize,
  onClose,
  showFeedback = true,
}: {
  connection: VoiceConnectionStatus;
  sessionState: VoiceSessionState;
  greetingComplete: boolean;
  onMinimize: () => void;
  onClose: () => void;
  /** False where the deployment has no one to read a rating. */
  showFeedback?: boolean;
}) {
  const intl = useIntl();
  return (
    <header className="flex shrink-0 items-center gap-2 border-b border-border/60 px-4 py-3">
      <span className="relative flex size-7 shrink-0 items-center justify-center">
        <HeidiLogoMark className="size-4 text-text-secondary" />
        <span
          className={cn(
            'absolute end-0 top-0 size-1.5 rounded-full ring-2 ring-surface-page',
            statusDotClass(connection)
          )}
          aria-hidden
        />
      </span>
      <div className="min-w-0 flex-1">
        <TypographyP2Bold className="truncate">
          {intl.formatMessage(voiceAssistantMessages.title)}
        </TypographyP2Bold>
        <TypographyCaption className="truncate text-text-secondary" aria-live="polite">
          {statusLabel(intl, connection, sessionState, greetingComplete)}
        </TypographyCaption>
      </div>
      {showFeedback ? <VoiceFeedbackThumbs size="sm" /> : null}
      <Button
        variant="ghost"
        size="sm"
        onClick={onMinimize}
        data-testid="voice-workspace-minimize"
      >
        <Minimize2 />
        {intl.formatMessage(messages.minimize)}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        aria-label={intl.formatMessage(messages.close)}
        onClick={onClose}
        data-testid="voice-workspace-close"
      >
        <X />
      </Button>
    </header>
  );
}

/** Idle shell only — drafts paint inline in the speak thread. */
export function VoiceWindowCanvas({
  showEmptyState,
  quickStartBusy,
  onQuickStart,
}: {
  showEmptyState: boolean;
  quickStartBusy: boolean;
  onQuickStart: (kind: WorkspaceQuickStartKind) => void;
}) {
  if (!showEmptyState) return null;
  return (
    <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 overflow-y-auto p-4 md:overflow-hidden md:p-6">
      <div className="min-h-0 md:overflow-hidden">
        <WorkspaceEmptyState onSelect={onQuickStart} busy={quickStartBusy} />
      </div>
    </div>
  );
}
